import { makeAutoObservable, runInAction, toJS, flow } from "mobx";
import { AccountInfo, Connection, LAMPORTS_PER_SOL, ParsedAccountData, PublicKey } from "@solana/web3.js";
import {
    Balance,
    BalanceMetadata,
    Service,
    SolanaBalance,
    Tariff,
    UriMetadata,
} from "../../../shared/src/Types/Types.ts";
import { GlobalStore } from "./GlobalStore.ts";
import { getTokenMetadata, TOKEN_2022_PROGRAM_ID, AccountLayout } from "@solana/spl-token";
import solana_logo from "../../../shared/src/img/solana-sol-logo.png";
import { parseAdditionalMetadata } from "../../../shared/src/Functions/Utils.ts";
import { RootStore } from "./RootStore.ts";
import BigNumber from "bignumber.js";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";

export type TabName = "Assets" | "Subscriptions" | "Tariffs";

type ParsedTokenAccounts = Array<{
    pubkey: PublicKey;
    account: AccountInfo<ParsedAccountData>;
}>;

export class WalletStore {
    globalStore: GlobalStore;
    walletPublicKey: PublicKey | undefined = undefined;

    fetchingLegacyBalancesMetadata: boolean = false;
    fetching2022BalancesMetadata: boolean = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.globalStore = rootStore.GlobalStore;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    subscribedAccounts = new Set<PublicKey>(); // Store subscribed account addresses
    subscriptionsToAccounts = new Map<PublicKey, number>(); // Map to store subscriptions for easy cleanup
    solanaSubscriptionId: number | undefined = undefined; // New property to track SOL subscription
    parsedTokenAccounts: ParsedTokenAccounts = [];
    subscryptoServices: Service[] = [];

    get totalWorth() {
        return this.balances.reduce((sum, balance) => sum + (balance.price_metadata?.worth_usd || 0), 0);
    }

    solBalance: SolanaBalance = {
        isSolana: true,
        amount: 0,
        decimals: 9,
        metadata: { name: "Solana", ticker: "SOL", logo_url: solana_logo },
        price_metadata: { price_usd: new BigNumber(0), worth_usd: 0 },
    };

    balances: Balance[] = [];

    // Utility to compare two balances deeply
    areBalancesEqual(balanceA: Balance, balanceB: Balance): boolean {
        return (
            balanceA.isSolana === balanceB.isSolana &&
            balanceA.isToken2022 === balanceB.isToken2022 &&
            balanceA.amount === balanceB.amount &&
            balanceA.decimals === balanceB.decimals &&
            balanceA.mint_address?.toString() === balanceB.mint_address?.toString() &&
            balanceA.ata_address?.toString() === balanceB.ata_address?.toString()
            // Add more fields if necessary, e.g., metadata, price_metadata
        );
    }

    clearBalances() {
        runInAction(() => {
            this.balances = [];
            this.solBalance.amount = 0;
        });
    }

    cleanupSubscriptions(connection: Connection) {
        if (this.solanaSubscriptionId !== undefined) {
            connection.removeAccountChangeListener(this.solanaSubscriptionId);
            console.log(`Unsubscribed from Solana account change: ${this.walletPublicKey?.toString()}`);
            this.solanaSubscriptionId = undefined; // Reset after cleanup
        }

        this.subscriptionsToAccounts.forEach((subscriptionId, pubkey) => {
            connection.removeAccountChangeListener(subscriptionId);
            console.log(`Unsubscribed from account change: ${pubkey.toString()}`);
        });
        this.subscriptionsToAccounts.clear();
        this.subscribedAccounts.clear();
    }

    async fetchSolana() {
        if (!this.walletPublicKey || !this.globalStore.isOnline) return;
        const solBalance = await this.globalStore.getConnection().getBalance(this.walletPublicKey);
        runInAction(() => {
            this.solBalance.amount = solBalance / LAMPORTS_PER_SOL;
        });

        // Remove any existing Solana subscription before creating a new one
        if (this.solanaSubscriptionId !== undefined) {
            await this.globalStore.getConnection().removeAccountChangeListener(this.solanaSubscriptionId);
            console.log(`Removed previous Solana subscription: ${this.solanaSubscriptionId}`);
        }

        // Subscribe to SOL balance changes and store the subscription ID
        this.solanaSubscriptionId = this.globalStore
            .getConnection()
            .onAccountChange(this.walletPublicKey, async (accountInfo) => {
                runInAction(() => {
                    this.solBalance.amount = accountInfo.lamports / LAMPORTS_PER_SOL;
                });
            });
    }

    tokenAccountsToBalances(tokenAccounts: ParsedTokenAccounts, isToken2022: boolean): Balance[] {
        return tokenAccounts.map(({ account, pubkey }) => {
            const amount = account.data.parsed.info.tokenAmount.uiAmount;
            const mintAddress = account.data.parsed.info.mint;
            const ataAddress = pubkey;
            const decimals = account.data.parsed.info.tokenAmount.decimals;

            return {
                isSolana: false,
                isToken2022,
                isSubscryptoEntity: undefined,
                amount,
                decimals,
                mint_address: new PublicKey(mintAddress),
                ata_address: new PublicKey(ataAddress),
                metadata: undefined,
                price_metadata: undefined,
                ...(isToken2022 && {
                    updateAuthority: undefined,
                    additionalMetadata: undefined,
                    uri: "",
                }),
            } as Balance;
        });
    }

    async fetchLegacyBalancesMetadata(balances: Balance[]): Promise<Balance[]> {
        const mintAddresses = balances.map((b) => b.mint_address);
        const response = await fetch("https://token-list-api.solana.cloud/v1/mints/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ addresses: mintAddresses }),
        });

        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        const tokensMetadata = await response.json();

        const metadataMap: Map<string, BalanceMetadata> = new Map(
            tokensMetadata.content.map((item: any) => [
                item.address,
                {
                    name: item.name,
                    ticker: item.symbol,
                    logo_url: item.logoURI,
                },
            ])
        );

        return balances.map((balance) => ({
            ...balance,
            metadata: metadataMap.get(balance.mint_address.toString()) || balance.metadata,
        }));
    }

    async fetchTokenPricesByMints(balances: Balance[]): Promise<Balance[]> {
        const mintList = balances.map((b) => b.mint_address.toString()).join(",");
        const response = await fetch(`https://api.geckoterminal.com/api/v2/networks/solana/tokens/multi/${mintList}`, {
            method: "GET",
            headers: { Accept: "application/json" },
        });
        const data = await response.json();

        return balances.map((balance) => {
            const token = data.data.find((item: any) => item.attributes.address === balance.mint_address.toString());
            const price = token ? new BigNumber(token.attributes.price_usd) : null;
            return {
                ...balance,
                price_metadata: {
                    price_usd: price || balance.price_metadata?.price_usd || null,
                    worth_usd: price
                        ? Number(price.multipliedBy(balance.amount).toFixed(2))
                        : balance.price_metadata?.worth_usd || null,
                },
            };
        });
    }

    getSubscryptoEntityAuthority(mintAddress: PublicKey, updateAuthority: PublicKey | undefined): boolean {
        if (updateAuthority === undefined) {
            console.error("updateAuthority undefined!", updateAuthority);
            return false;
        }
        const programId = this.globalStore.program?.programId;
        if (!programId) {
            throw new Error("No program id found");
        }
        const nft_authority_public_key = PublicKey.findProgramAddressSync(
            [Buffer.from("nft_authority"), mintAddress.toBuffer()],
            programId
        )[0];
        return nft_authority_public_key.toString() === updateAuthority.toString(); // comparing raw PublicKey objects doesn't work
    }

    async fetchToken2022Metadata(balances: Balance[]): Promise<Extract<Balance, { isToken2022: true }>[]> {
        const fetchMetadataTasks = balances
            .filter((balance) => balance.isToken2022)
            .map(async (balance) => {
                try {
                    const metadata = await getTokenMetadata(this.globalStore.getConnection(), balance.mint_address);

                    // If metadata is null, set it to null explicitly
                    if (!metadata) {
                        return {
                            ...balance,
                            additionalMetadata: null,
                            uri: null,
                            updateAuthority: null,
                            metadata: null,
                        };
                    }

                    const isSubscryptoEntity = this.getSubscryptoEntityAuthority(
                        balance.mint_address,
                        metadata.updateAuthority
                    );

                    // TODO: проверить этот код весь на логичность и последовательность ещё раз. Странный тут какой-то
                    let metadataFromUri: UriMetadata = { image: undefined };
                    try {
                        metadataFromUri = await this.fetchMetadataByUri(metadata.uri);
                    } catch (e) {
                        console.error("Could not fetch metadata", e);
                    }

                    return {
                        ...balance,
                        isSubscryptoEntity: isSubscryptoEntity,
                        additionalMetadata: metadata.additionalMetadata
                            ? parseAdditionalMetadata(metadata.additionalMetadata)
                            : null,
                        uri: metadata.uri,
                        updateAuthority: metadata.updateAuthority,
                        metadata: {
                            name: metadata.name || "",
                            ticker: metadata.symbol || "",
                            logo_url: metadataFromUri.image || "",
                        },
                    };
                } catch (e) {
                    console.error("Error fetching metadata for balance:", balance, e);
                    return balance; // Return the original balance if there's an error
                }
            });

        // Run all metadata fetch tasks in parallel
        return await Promise.all(fetchMetadataTasks);
    }

    async updateLegacyBalancesMetadata(): Promise<void> {
        if (this.fetchingLegacyBalancesMetadata) {
            return;
        }
        runInAction(() => {
            this.fetchingLegacyBalancesMetadata = true;
        });
        try {
            const legacyBalancesWithMetadata: Balance[] = await this.fetchLegacyBalancesMetadata(this.balances);
            // console.log("legacyBalancesWithMetadata", legacyBalancesWithMetadata);

            const metadataMap = new Map(
                legacyBalancesWithMetadata.map((balance) => [balance.mint_address.toString(), balance.metadata])
            );
            runInAction(() => {
                this.balances = this.balances.map((balance) => {
                    const newMetadata = metadataMap.get(balance.mint_address.toString());
                    return {
                        ...balance, // Spread the existing balance
                        metadata: newMetadata ?? balance.metadata, // Update metadata if found, otherwise keep existing
                    };
                });
            });
        } catch (error) {
            console.error("Could not fetch legacy Metadata", error);
        } finally {
            runInAction(() => {
                this.fetchingLegacyBalancesMetadata = false;
            });
        }
    }

    async updateSolanaMainnetBalancesMetadata(balances: Balance[]) {
        const mintList = balances.map((b) => b.mint_address.toString()).join(",");
        const response = await fetch(`https://api.geckoterminal.com/api/v2/networks/solana/tokens/multi/${mintList}`, {
            method: "GET",
            headers: { Accept: "application/json" },
        });
        const data = await response.json();

        return balances.map((balance) => {
            const token = data.data.find((item: any) => item.attributes.address === balance.mint_address.toString());

            const price = token ? new BigNumber(token.attributes.price_usd) : null;
            const updatedMetadata = token
                ? {
                      name: token.attributes.name,
                      ticker: token.attributes.symbol,
                      logo_url: token.attributes.image_url || null,
                  }
                : balance.metadata;

            return {
                ...balance,
                metadata: updatedMetadata,
                price_metadata: {
                    price_usd: price || balance.price_metadata?.price_usd || null,
                    worth_usd: price
                        ? Number(price.multipliedBy(balance.amount).toFixed(2))
                        : balance.price_metadata?.worth_usd || null,
                },
            };
        });
    }

    async update2022BalancesMetadata(): Promise<void> {
        if (this.fetching2022BalancesMetadata) {
            return;
        }
        runInAction(() => {
            this.fetching2022BalancesMetadata = true;
        });

        try {
            const balances2022WithMetadata = await this.fetchToken2022Metadata(this.balances);
            // console.log("balances2022WithMetadata", balances2022WithMetadata);

            await this.parseSubscryptoServiceBalances(balances2022WithMetadata);

            // Create a map of the balances, keyed by mint_address
            const updatedBalancesMap = new Map(
                balances2022WithMetadata.map((balance) => [balance.mint_address.toString(), balance])
            );

            runInAction(() => {
                this.balances = this.balances.map((balance) => {
                    const updatedBalance = updatedBalancesMap.get(balance.mint_address.toString());
                    return updatedBalance ? updatedBalance : balance; // Use updated balance if available, otherwise keep original
                });
            });
        } catch (error) {
            console.error("Could not fetch legacy Metadata", error);
        } finally {
            runInAction(() => {
                this.fetching2022BalancesMetadata = false;
            });
        }
    }

    async parseSubscryptoServiceBalances(balances: Extract<Balance, { isToken2022: true }>[]) {
        const program = this.globalStore.program;
        if (!program) {
            throw new Error("Program is null");
        }

        const services: Service[] = [];
        for (const balance of balances) {
            if (
                balance.isSubscryptoEntity &&
                balance.additionalMetadata &&
                balance.additionalMetadata.entity_type === "service"
            ) {
                const service_tariff_list_pda = PublicKey.findProgramAddressSync(
                    [Buffer.from("service_tariffs"), balance.mint_address.toBuffer()],
                    program.programId
                );
                if (service_tariff_list_pda[0].toString() !== balance.additionalMetadata.tariffs_list) {
                    console.error(
                        "tariffs_list public key is not valid! Should be: ",
                        service_tariff_list_pda[0].toString(),
                        " instead got the ",
                        balance.additionalMetadata.tariffs_list
                    );
                }

                const service_tariff_list = await program.account.serviceTariffList.fetch(service_tariff_list_pda[0]);

                const tariffs: Tariff[] = [];
                for (const tariff_key of service_tariff_list.tariffs) {
                    const tariff_balance = balances.find(
                        (tariff) => tariff.mint_address.toString() === tariff_key.toString()
                    );
                    if (!tariff_balance) {
                        throw new Error("Tariff token not found");
                    }
                    if (
                        tariff_balance.isToken2022 &&
                        tariff_balance.isSubscryptoEntity &&
                        tariff_balance.additionalMetadata &&
                        tariff_balance.additionalMetadata.entity_type === "tariff"
                    ) {
                        const tariff: Tariff = {
                            address: tariff_balance.mint_address,
                            description: tariff_balance.additionalMetadata.description,
                            periodicity: Number(tariff_balance.additionalMetadata.periodicity),
                            price: Number(tariff_balance.additionalMetadata.price),
                            subscriberDataDescription: tariff_balance.additionalMetadata.subscriberDataDescription,
                            title: tariff_balance.additionalMetadata.title,
                        };
                        tariffs.push(tariff);
                    }
                }

                console.log("balance", balance);
                const service: Service = {
                    address: balance.mint_address,
                    backgroundLink: balance.additionalMetadata.background_link,
                    description: balance.additionalMetadata.description,
                    logoLink: balance.additionalMetadata.logo_link,
                    tariffs: service_tariff_list.tariffs,
                    tariffs_metadatas: tariffs,
                    title: balance.additionalMetadata.title,
                };
                services.push(service);
            }
        }
        runInAction(() => {
            this.subscryptoServices = services;
        });
        console.log("services", services);
    }

    async fetchMetadataByUri(uri: string): Promise<UriMetadata> {
        const response = await fetch(uri, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
        });
        if (!response.ok) {
            throw new Error(`Server responded with status: ${response.status}`);
        }

        return await response.json();
    }

    private haveTokenAccountsChanged(newAccounts: any[], oldAccounts: any[]): boolean {
        if (newAccounts.length !== oldAccounts.length) return true;
        const newKeys = new Set(newAccounts.map((acc) => acc.pubkey.toBase58()));
        return oldAccounts.some((acc) => !newKeys.has(acc.pubkey.toBase58()));
    }

    private haveBalancesChanged(currentBalances: Balance[], newBalances: Balance[]): boolean {
        if (currentBalances.length !== newBalances.length) return true;
        return currentBalances.some((current, i) => !this.areBalancesEqual(current, newBalances[i]));
    }

    fetchBalances = flow(function* (this: WalletStore) {
        if (!this.walletPublicKey || !this.globalStore.isOnline) return;
        console.log("fetchBalances");

        const [tokenLegacyAccounts, token2022Accounts] = yield Promise.all([
            this.globalStore.getConnection().getParsedTokenAccountsByOwner(this.walletPublicKey, {
                programId: TOKEN_PROGRAM_ID,
            }),
            this.globalStore.getConnection().getParsedTokenAccountsByOwner(this.walletPublicKey, {
                programId: TOKEN_2022_PROGRAM_ID,
            }),
        ]);
        const tokenAccounts = [...tokenLegacyAccounts.value, ...token2022Accounts.value];

        // Check if token accounts have changed
        if (!this.haveTokenAccountsChanged(tokenAccounts, this.parsedTokenAccounts)) {
            return; // No changes, skip update
        }

        runInAction(() => {
            this.parsedTokenAccounts = tokenAccounts;
        });

        let newBalances = [
            ...this.tokenAccountsToBalances(tokenLegacyAccounts.value, false),
            ...this.tokenAccountsToBalances(token2022Accounts.value, true),
        ];

        if (newBalances.length > 0 && this.globalStore.network == "mainnet-beta") {
            newBalances = yield this.fetchTokenPricesByMints(newBalances);
        }

        // Update subscriptions
        for (const balance of newBalances) {
            const ataAddress = balance.ata_address;
            if (!this.subscribedAccounts.has(balance.ata_address)) {
                this.subscribedAccounts.add(balance.ata_address);
                const subscriptionId = this.globalStore
                    .getConnection()
                    .onAccountChange(balance.ata_address, async (accountInfo) => {
                        console.log(`Account ${balance.ata_address} changed`);
                        runInAction(() => {
                            const index = this.balances.findIndex(
                                (b) => b.ata_address.toString() === ataAddress.toString()
                            );
                            if (index === -1) return; // Balance no longer exists

                            try {
                                const accountData = AccountLayout.decode(new Uint8Array(accountInfo.data));
                                const rawAmount = accountData.amount; // BN
                                const decimals = this.balances[index].decimals;
                                const uiAmount = Number(rawAmount.toString()) / Math.pow(10, decimals);

                                if (uiAmount > 0) {
                                    // Update balance amount and worth_usd
                                    this.balances[index].amount = uiAmount;
                                    if (this.balances[index].price_metadata?.price_usd) {
                                        this.balances[index].price_metadata.worth_usd = Number(
                                            this.balances[index].price_metadata.price_usd
                                                .multipliedBy(uiAmount)
                                                .toFixed(2)
                                        );
                                    }
                                } else {
                                    // Account likely closed; remove balance and subscription
                                    const subscriptionId = this.subscriptionsToAccounts.get(ataAddress);
                                    if (subscriptionId !== undefined) {
                                        this.globalStore.getConnection().removeAccountChangeListener(subscriptionId);
                                        this.subscriptionsToAccounts.delete(ataAddress);
                                        this.subscribedAccounts.delete(ataAddress);
                                    }
                                    this.balances.splice(index, 1);
                                }
                            } catch (error) {
                                console.error(`Failed to parse account data for ${ataAddress}:`, error);
                                // Optionally remove balance if parsing fails (e.g., invalid data)
                                const subscriptionId = this.subscriptionsToAccounts.get(ataAddress);
                                if (subscriptionId !== undefined) {
                                    this.globalStore.getConnection().removeAccountChangeListener(subscriptionId);
                                    this.subscriptionsToAccounts.delete(ataAddress);
                                    this.subscribedAccounts.delete(ataAddress);
                                }
                                this.balances.splice(index, 1);
                            }
                        });
                    });
                this.subscriptionsToAccounts.set(balance.ata_address, subscriptionId);
            }
        }

        // Update balances in place, only if there are changes
        const currentBalancesMap = new Map(this.balances.map((b) => [b.mint_address.toString(), b]));
        // const newBalancesMap = new Map(newBalances.map((b) => [b.mint_address.toString(), b]));

        const updatedBalances: Balance[] = [];
        const seenKeys = new Set<string>();

        // Process all mints from newBalances
        newBalances.forEach((newBalance) => {
            const key = newBalance.mint_address.toString();
            seenKeys.add(key);
            const currentBalance = currentBalancesMap.get(key);

            if (!currentBalance || !this.areBalancesEqual(currentBalance, newBalance)) {
                updatedBalances.push({ ...newBalance }); // Deep copy to avoid reference issues
            } else {
                updatedBalances.push(currentBalance); // Reuse existing balance if unchanged
            }
        });

        // Add any balances that are in current but not in new (if applicable)
        this.balances.forEach((currentBalance) => {
            const key = currentBalance.mint_address.toString();
            if (!seenKeys.has(key)) {
                updatedBalances.push(currentBalance);
            }
        });

        if (this.haveBalancesChanged(this.balances, updatedBalances)) {
            this.balances = updatedBalances;
            console.log("balances updated", toJS(this.balances));

            if (this.rootStore.GlobalStore.network === "mainnet-beta") {
                this.balances = yield this.updateSolanaMainnetBalancesMetadata(this.balances);
                console.log("updated metadata via new function on mainnet");
                return;
            }

            // Trigger metadata updates only on actual balance changes
            Promise.allSettled([this.updateLegacyBalancesMetadata(), this.update2022BalancesMetadata()]).then(() =>
                console.log("Metadata updates completed")
            );
        }
    });

    balanceFetchInterval: ReturnType<typeof setInterval> | undefined;

    async startFetchingBalances(): Promise<void> {
        this.walletPublicKey = new PublicKey(this.globalStore.fetchPlatformData("solana", "active_key"));
        await Promise.all([this.fetchSolana(), this.fetchBalances()]);

        // Clear previous interval if it exists
        if (this.balanceFetchInterval) {
            clearInterval(this.balanceFetchInterval);
        }

        // Set a new interval for fetching balances
        this.balanceFetchInterval = setInterval(async () => {
            try {
                await this.fetchBalances();
            } catch (error) {
                console.error("Error fetching balances:", error);
            }
        }, 20000); // Fetch balances every 20 seconds
    }
}
